import { createAction } from '@reduxjs/toolkit';
import { normalizeError } from '@packages/shared/core/common/utils/errors';
import { FlowName } from '@packages/shared/core/quiz/entities';
import { AppThunk } from '@packages/shared/core/common/store';
import { Expert, ExpertReview, ExpertCategory, ExpertRequestFilter, ExpertsRequestParams } from '../entities';

export const currentExpertUpdated = createAction<string | null>('expert/CURRENT_EXPERT_UPDATED');

export const fetchInitialExpertsPending = createAction('experts/FETCH_INITIAL_EXPERTS/pending');
export const fetchInitialExpertsSucceed = createAction<Array<Expert>>('experts/FETCH_INITIAL_EXPERTS/succeed');
export const fetchInitialExpertsFailed = createAction('experts/FETCH_INITIAL_EXPERTS/failed');

export const fetchAllExpertsPending = createAction('experts/FETCH_ALL_EXPERTS/pending');
export const fetchAllExpertsSucceed = createAction<Array<Expert>>('experts/FETCH_ALL_EXPERTS/succeed');
export const fetchAllExpertsFailed = createAction('experts/FETCH_ALL_EXPERTS/failed');

export const fetchExpertsByCategoriesPending = createAction('experts/FETCH_EXPERTS_BY_CATEGORIES/pending');
export const fetchExpertsByCategoriesSucceed = createAction<Array<Expert>>(
  'experts/FETCH_EXPERTS_BY_CATEGORIES/succeed',
);
export const fetchExpertsByCategoriesFailed = createAction('experts/FETCH_EXPERTS_BY_CATEGORIES/failed');

export const fetchExpertsByCategoryPending = createAction('experts/FETCH_EXPERTS_BY_CATEGORY/pending');
export const fetchExpertsByCategorySucceed = createAction<Array<Expert>>('experts/FETCH_EXPERTS_BY_CATEGORY/succeed');
export const fetchExpertsByCategoryFailed = createAction('experts/FETCH_EXPERTS_BY_CATEGORY/failed');

export const fetchRandomExpertPending = createAction('experts/FETCH_RANDOM_EXPERT/pending');
export const fetchRandomExpertSucceed = createAction<{
  info: Expert | null;
  reviews: Array<ExpertReview> | null;
}>('experts/FETCH_RANDOM_EXPERT/succeed');
export const fetchRandomExpertFailed = createAction('experts/FETCH_RANDOM_EXPERT/failed');

export const fetchExpertReviewsPending = createAction('expert/FETCH_EXPERT_REVIEWS/pending');
export const fetchExpertReviewsSucceed = createAction<Array<ExpertReview>>('expert/FETCH_EXPERT_REVIEWS/succeed');
export const fetchExpertReviewsFailed = createAction('expert/FETCH_EXPERT_REVIEWS/failed');

export const fetchInitialExperts =
  ({
    perPage,
    customerSource,
    filter,
  }: {
    perPage: number;
    customerSource: FlowName;
    filter?: ExpertRequestFilter;
  }): AppThunk =>
  async (dispatch, getState, extra) => {
    const { expertService, logger } = extra;

    dispatch(fetchInitialExpertsPending());

    try {
      const experts = await expertService.getExperts({
        perPage,
        page: 1,
        customerSource,
        filter,
      });
      dispatch(fetchInitialExpertsSucceed(experts));
    } catch (e) {
      const err = normalizeError(e);
      logger.error(err);
      dispatch(fetchInitialExpertsFailed());
    }
  };

export const fetchAllExperts =
  (params: ExpertsRequestParams): AppThunk =>
  async (dispatch, getState, extra) => {
    const { expertService, logger } = extra;
    dispatch(fetchAllExpertsPending());
    try {
      const experts = await expertService.getExperts(params);
      dispatch(fetchAllExpertsSucceed(experts));
    } catch (e) {
      const err = normalizeError(e);
      logger.error(err);
      dispatch(fetchAllExpertsFailed());
    }
  };

export const fetchExpertsByCategories =
  ({ customerSource }: { categories: Array<ExpertCategory>; customerSource: FlowName }): AppThunk =>
  async (dispatch, getState, extra) => {
    const { expertService, logger } = extra;

    dispatch(fetchExpertsByCategoriesPending());
    try {
      const experts = await expertService.getExperts({ customerSource });
      dispatch(fetchExpertsByCategoriesSucceed(experts));
    } catch (e) {
      const err = normalizeError(e);
      logger.error(err);
      dispatch(fetchExpertsByCategoriesFailed());
    }
  };

export const fetchExpertsByCategory =
  ({ customerSource }: { category: ExpertCategory; customerSource: FlowName }): AppThunk =>
  async (dispatch, getState, extra) => {
    const { expertService, logger } = extra;
    dispatch(fetchExpertsByCategoryPending());
    try {
      const experts = await expertService.getExperts({ customerSource });
      dispatch(fetchExpertsByCategorySucceed(experts));
    } catch (e) {
      const err = normalizeError(e);
      logger.error(err);
      dispatch(fetchExpertsByCategoryFailed());
    }
  };

export const fetchRandomExpert =
  (customerSource: FlowName | 'seo'): AppThunk =>
  async (dispatch, getState, extra) => {
    const { expertService, logger } = extra;
    dispatch(fetchRandomExpertPending());
    try {
      const expert = await expertService.getRandomExpert({ customerSource });
      const reviews = await expertService.getExpertReviews(expert.id, {
        filter: {
          withText: true,
          processingStatus: 'published',
        },
      });
      dispatch(fetchRandomExpertSucceed({ info: expert, reviews }));
    } catch (e) {
      const err = normalizeError(e);
      logger.error(err);
      dispatch(fetchRandomExpertFailed());
    }
  };

export const fetchExpertReviews =
  (expertId: string): AppThunk =>
  async (dispatch, getState, extra) => {
    const { expertService, logger } = extra;
    dispatch(fetchExpertReviewsPending());
    try {
      const reviews = await expertService.getExpertReviews(expertId, {
        filter: {
          withText: true,
          processingStatus: 'published',
        },
      });
      dispatch(fetchExpertReviewsSucceed(reviews));
    } catch (e) {
      const err = normalizeError(e);
      logger.error(err);
      dispatch(fetchExpertReviewsFailed());
    }
  };
